import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

/* ── Galaxy Particle background ──────────────────────────────────── */
function ParticleCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;

    const BALL_NUM = 70;
    const R = 2;
    const ALPHA_F = 0.025;
    const DIS_LIMIT = 280;
    const LINE_WIDTH = 0.7;
    // brand olive-green for particles; orange accent for lines near mouse
    const BALL_COLOR = { r: 148, g: 162, b: 78 };
    const LINE_COLOR = { r: 148, g: 162, b: 78 };
    const MOUSE_LINE_COLOR = { r: 217, g: 162, b: 98 };

    let balls = [];
    let mouse_in = false;
    const mouse_ball = { x: 0, y: 0, vx: 0, vy: 0, r: 0, type: 'mouse' };

    function initCanvas() {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    }

    function rnd(min, max) { return Math.random() * (max - min) + min; }
    function rndSide(len)  { return Math.ceil(Math.random() * len); }
    function pick(arr)     { return arr[Math.floor(Math.random() * arr.length)]; }

    function randomSpeed(pos) {
      const lo = -1, hi = 1;
      if (pos === 'top')    return [rnd(lo, hi), rnd(0.1, hi)];
      if (pos === 'right')  return [rnd(lo, -0.1), rnd(lo, hi)];
      if (pos === 'bottom') return [rnd(lo, hi), rnd(lo, -0.1)];
                            return [rnd(0.1, hi), rnd(lo, hi)];
    }

    function newBall() {
      const pos = pick(['top', 'right', 'bottom', 'left']);
      const [vx, vy] = randomSpeed(pos);
      const coords = {
        top:    { x: rndSide(canvas.width),  y: -R },
        right:  { x: canvas.width + R,        y: rndSide(canvas.height) },
        bottom: { x: rndSide(canvas.width),  y: canvas.height + R },
        left:   { x: -R,                      y: rndSide(canvas.height) },
      };
      return { ...coords[pos], vx, vy, r: R, alpha: 1, phase: rnd(0, 10) };
    }

    function initBalls(num) {
      for (let i = 0; i < num; i++) {
        balls.push({
          x: rndSide(canvas.width),
          y: rndSide(canvas.height),
          vx: randomSpeed('top')[0],
          vy: randomSpeed('top')[1],
          r: R,
          alpha: 1,
          phase: rnd(0, 10),
        });
      }
    }

    function dist(a, b) {
      const dx = a.x - b.x, dy = a.y - b.y;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function renderBalls() {
      balls.forEach(b => {
        if (b.type) return;
        ctx.fillStyle = `rgba(${BALL_COLOR.r},${BALL_COLOR.g},${BALL_COLOR.b},${b.alpha})`;
        ctx.beginPath();
        ctx.arc(b.x, b.y, R, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.fill();
      });
    }

    function renderLines() {
      for (let i = 0; i < balls.length; i++) {
        for (let j = i + 1; j < balls.length; j++) {
          const d = dist(balls[i], balls[j]);
          if (d >= DIS_LIMIT) continue;
          const alpha = ((1 - d / DIS_LIMIT) * 0.35).toFixed(3);
          // orange accent when either endpoint is the mouse ball
          const isMouse = balls[i].type === 'mouse' || balls[j].type === 'mouse';
          const c = isMouse ? MOUSE_LINE_COLOR : LINE_COLOR;
          ctx.strokeStyle = `rgba(${c.r},${c.g},${c.b},${alpha})`;
          ctx.lineWidth = LINE_WIDTH;
          ctx.beginPath();
          ctx.moveTo(balls[i].x, balls[i].y);
          ctx.lineTo(balls[j].x, balls[j].y);
          ctx.stroke();
          ctx.closePath();
        }
      }
    }

    function updateBalls() {
      const kept = [];
      balls.forEach(b => {
        b.x += b.vx;
        b.y += b.vy;
        if (b.x > -50 && b.x < canvas.width + 50 && b.y > -50 && b.y < canvas.height + 50) {
          kept.push(b);
        }
        b.phase += ALPHA_F;
        b.alpha = Math.abs(Math.cos(b.phase));
      });
      balls = kept;
    }

    function addIfNeeded() {
      if (balls.length < BALL_NUM) balls.push(newBall());
    }

    function render() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      renderBalls();
      renderLines();
      updateBalls();
      addIfNeeded();
      animId = requestAnimationFrame(render);
    }

    initCanvas();
    initBalls(BALL_NUM);
    animId = requestAnimationFrame(render);

    const onResize = () => initCanvas();
    window.addEventListener('resize', onResize);

    // Mouse events on the hero section (canvas has pointer-events:none so it can't catch them directly)
    const hero = canvas.parentElement;

    const onEnter = () => { mouse_in = true;  balls.push(mouse_ball); };
    const onLeave = () => { mouse_in = false; balls = balls.filter(b => !b.type); };
    const onMove  = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse_ball.x = e.clientX - rect.left;
      mouse_ball.y = e.clientY - rect.top;
    };

    hero.addEventListener('mouseenter', onEnter);
    hero.addEventListener('mouseleave', onLeave);
    hero.addEventListener('mousemove',  onMove);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
      hero.removeEventListener('mouseenter', onEnter);
      hero.removeEventListener('mouseleave', onLeave);
      hero.removeEventListener('mousemove',  onMove);
    };
  }, []);

  return <canvas ref={canvasRef} className="particle-canvas" />;
}

/* ── Animated counter ────────────────────────────────────────────── */
function Counter({ target, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          setCount(Math.floor((1 - Math.pow(1 - p, 3)) * target));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);
  return <span ref={ref}>{count}{suffix}</span>;
}

/* ── Icons ───────────────────────────────────────────────────────── */
const IconUsers = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
  </svg>
);
const IconCloud = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
  </svg>
);
const IconGraduate = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
);
const IconShield = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const IconActivity = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
);
const IconLock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
);
const IconEye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
);

/* ── Security Console (hero right panel) ─────────────────────────── */
function SecurityConsole() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 3000);
    return () => clearInterval(id);
  }, []);

  const activities = [
    { icon: <IconCheck />, text: 'Vulnerability scan complete', time: '0s ago',   color: 'green' },
    { icon: <IconCheck />, text: 'Threat intelligence updated', time: '12s ago',  color: 'green' },
    { icon: <IconCheck />, text: 'Compliance check passed',     time: '38s ago',  color: 'green' },
    { icon: <IconEye />,   text: 'Real-time monitoring active', time: 'ongoing',  color: 'orange' },
  ];

  return (
    <div className="console-card">
      {/* Header bar */}
      <div className="console-header">
        <div className="console-header-left">
          <img src="/logo.png" alt="ThirdEye" className="console-logo" />
          <div>
            <div className="console-title">Security Console</div>
            <div className="console-sub">ThirdEye Cybersecurity</div>
          </div>
        </div>
        <div className="console-live">
          <span className="live-dot" />
          LIVE
        </div>
      </div>

      {/* Scan line */}
      <div className="console-scan-wrap">
        <div className="console-scan-line" />
      </div>

      {/* Central shield */}
      <div className="console-shield-wrap">
        <div className="pulse-ring pulse-ring-1" />
        <div className="pulse-ring pulse-ring-2" />
        <div className="pulse-ring pulse-ring-3" />
        <div className="console-shield-icon">
          <IconShield />
        </div>
        <div className="shield-status">
          <span className="shield-dot" />
          ALL SYSTEMS SECURED
        </div>
      </div>

      {/* Status grid */}
      <div className="console-status-grid">
        <div className="status-tile status-tile--green">
          <div className="status-tile-icon"><IconLock /></div>
          <div className="status-tile-val">247</div>
          <div className="status-tile-lbl">Systems Protected</div>
        </div>
        <div className="status-tile status-tile--orange">
          <div className="status-tile-icon"><IconActivity /></div>
          <div className="status-tile-val">0</div>
          <div className="status-tile-lbl">Active Threats</div>
        </div>
        <div className="status-tile status-tile--green">
          <div className="status-tile-icon"><IconEye /></div>
          <div className="status-tile-val">99.9<span style={{fontSize:'12px'}}>%</span></div>
          <div className="status-tile-lbl">Uptime</div>
        </div>
      </div>

      {/* Activity feed */}
      <div className="console-activity">
        <div className="activity-header">
          <span>Recent Activity</span>
          <span className="activity-count">{tick % 10 + 4} events</span>
        </div>
        <div className="activity-list">
          {activities.map((a, i) => (
            <div className={`activity-row activity-row--${a.color}`} key={i}>
              <span className="activity-icon">{a.icon}</span>
              <span className="activity-text">{a.text}</span>
              <span className="activity-time">{a.time}</span>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

/* ── Main Home component ─────────────────────────────────────────── */
export default function Home() {
  return (
    <div className="home">
      {/* HERO */}
      <section className="hero">
        <ParticleCanvas />
        <div className="hero-grid-overlay" />
        <div className="container hero-inner">
          {/* Left column */}
          <div className="hero-left">
            <div className="hero-badge">
              <span className="badge-dot" />
              Trusted Cybersecurity Partner
            </div>

            <h1 className="hero-title">
              Build Your<br />
              <span className="gradient-text">Cyber Security</span><br />
              Resilience
            </h1>

            <p className="hero-desc">
              ThirdEye Cybersecurity empowers organizations to navigate the digital landscape securely - through expert training, elite staff augmentation, and robust IT services.
            </p>

            <div className="hero-actions">
              <Link to="/contact" className="hero-btn-ghost">
                Get a Consultation
              </Link>
              <Link to="/it-services" className="btn-primary hero-btn-primary">
                Explore Services <IconArrow />
              </Link>
            </div>

            <div className="hero-trust-row">
              {[
                { icon: <IconGraduate />, label: 'Expert-Led Training' },
                { icon: <IconUsers />,   label: 'Elite Talent Pool' },
                { icon: <IconShield />,  label: 'End-to-End Security' },
              ].map((f, i) => (
                <div className="hero-trust-item" key={i}>
                  <span className="trust-icon">{f.icon}</span>
                  <span>{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right column */}
          <div className="hero-right">
            <SecurityConsole />
          </div>
        </div>

        {/* Stats strip — anchored to hero bottom */}
        <div className="hero-stats-strip">
          <div className="container">
            <div className="stats-grid">
              {[
                { value: 9,   suffix: '+', label: 'Certification Programs' },
                { value: 100, suffix: '+', label: 'Professionals Placed' },
                { value: 3,   suffix: '',  label: 'Core Service Pillars' },
                { value: 5,   suffix: '+', label: 'Years of Experience' },
              ].map((s, i) => (
                <div className="stat-card" key={i}>
                  <div className="stat-number"><Counter target={s.value} suffix={s.suffix} /></div>
                  <div className="stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="services-section" id="services">
        <div className="container">
          <div className="section-header">
            <span className="section-label">What We Do</span>
            <h2 className="section-title">Comprehensive <span>Security Solutions</span></h2>
            <p className="section-desc">
              From bridging knowledge gaps through expert training to deploying top-tier security talent and delivering scalable IT infrastructure.
            </p>
          </div>
          <div className="services-grid">
            <ServiceCard icon={<IconGraduate />} color="orange" label="Training"
              title="Cybersecurity & IT Training"
              desc="Bridge the knowledge gap with hands-on, industry-relevant training programs. From CompTIA Security+ to CISSP - we prepare professionals at every level."
              features={['9 Certification Programs','Hands-on Labs','Expert Instructors','Flexible Learning Paths']} link="/training" />
            <ServiceCard icon={<IconUsers />} color="orange" label="Staff Augmentation"
              title="Elite Security Talent"
              desc="Seamlessly integrate vetted cybersecurity professionals into your team. Incident responders, penetration testers, security architects, and more."
              features={['Pre-screened Experts','Rapid Deployment','Cultural Fit Matching','Ongoing Support']} link="/staff-augmentation" />
            <ServiceCard icon={<IconCloud />} color="orange" label="IT Services"
              title="Robust Digital Solutions"
              desc="Deliver secure, scalable digital infrastructure. Cloud migration, threat detection, compliance management, and end-to-end IT support."
              features={['Cloud Security','Threat Detection','Compliance Management','Network Infrastructure']} link="/it-services" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="hiw-section">
        <div className="hiw-bg-glow" />
        <div className="container">
          <div className="section-header centered">
            <span className="section-label hiw-label">Our Process</span>
            <h2 className="section-title hiw-title">How It <span>Works</span></h2>
            <p className="section-desc hiw-desc">
              A clear three-step journey - from learning the craft to landing your next cybersecurity role.
            </p>
          </div>
          <div className="hiw-steps">
            {[
              { n: '01', img: '/team-female.webp',      title: 'Learn',    desc: 'Study under active industry professionals with hands-on labs, real-world scenarios, and curriculum mapped to top certifications.' },
              { n: '02', img: '/security-analyst.webp', title: 'Practice', desc: 'Sharpen your edge with mock interviews, practical assessments, and direct coaching from seasoned cybersecurity experts.' },
              { n: '03', img: '/ai-developer.webp',     title: 'Apply',    desc: 'Access our curated job network and direct placement pipelines to top organizations seeking certified security talent.' },
            ].map((step, i) => (
              <div className="hiw-step" key={i}>
                <div className="hiw-visual hiw-visual--photo" style={{ backgroundImage: `url(${step.img})` }}>
                  <span className="hiw-bg-num">{step.n}</span>
                  <div className="hiw-photo-overlay" />
                </div>
                <div className="hiw-body">
                  <span className="hiw-tag">Step {step.n}</span>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
                <div className="hiw-foot" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="why-section">
        <div className="container">
          <div className="why-inner">
            <div className="why-left">
              <span className="section-label">Why ThirdEye</span>
              <h2 className="section-title">Your Trusted <span>Security Partner</span></h2>
              <p className="section-desc">
                We do not just provide services - we build long-term partnerships rooted in trust, expertise, and measurable results.
              </p>
              <div className="why-items">
                {[
                  { img: null, title: 'Client-Centric Approach',      desc: 'Tailored solutions aligned precisely with your business objectives and security posture.' },
                  { img: null, title: 'Expertise in Action',          desc: 'Experienced professionals with proven track records across diverse sectors and industries.' },
                  { img: null,          title: 'Industry-Relevant Curriculum', desc: 'Programs developed collaboratively with industry leaders emphasizing real-world applications.' },
                  { img: null,          title: 'Quality Assurance',            desc: 'Rigorous candidate assessment and ongoing support throughout every engagement.' },
                ].map((item, i) => (
                  <div className="why-item" key={i}>
                    <div className="why-icon">
                      {item.img
                        ? <img src={item.img} alt={item.title} className="why-apart-img" />
                        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      }
                    </div>
                    <div><h4>{item.title}</h4><p>{item.desc}</p></div>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="btn-primary" style={{ marginTop: '8px' }}>
                Partner With Us <IconArrow />
              </Link>
            </div>
            <div className="why-right">
              <div className="why-photo-wrap">
                <img src="/security-brain.webp" alt="Cybersecurity Intelligence" className="why-photo" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="certs-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Training Programs</span>
            <h2 className="section-title">Industry <span>Certifications</span></h2>
          </div>
          <div className="certs-grid">
            {['CEH','CISSP','CompTIA Security+','CCSP','AWS Security','CND','Cisco CyberOps','CFR','WAST'].map((cert, i) => (
              <div className="cert-pill" key={i}>{cert}</div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/training" className="btn-primary">View All Programs <IconArrow /></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-inner">
            <div className="cta-glow" />
            <div className="cta-content">
              <h2>Ready to Strengthen Your Security Posture?</h2>
              <p>Let us discuss how ThirdEye Cybersecurity can empower your team and protect your organization.</p>
              <div className="cta-actions">
                <Link to="/jobs" className="btn-outline-white">View Open Roles</Link>
                <Link to="/contact" className="btn-primary">Contact Us Today <IconArrow /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ── Service Card ────────────────────────────────────────────────── */
function ServiceCard({ icon, color, label, title, desc, features, link }) {
  return (
    <div className={`service-card service-card--${color}`}>
      <div className="service-card-header">
        <div className={`service-icon service-icon--${color}`}>{icon}</div>
        <span className="service-label">{label}</span>
      </div>
      <h3 className="service-title">{title}</h3>
      <p className="service-desc">{desc}</p>
      <ul className="service-features">
        {features.map(f => (
          <li key={f}><span className="feature-dot" />{f}</li>
        ))}
      </ul>
      <Link to={link} className={`service-link service-link--${color}`}>
        Learn More <IconArrow />
      </Link>
    </div>
  );
}

/* ── Why Visual ──────────────────────────────────────────────────── */
function WhyVisual() {
  return (
    <div className="why-svg-wrap">
      <svg viewBox="0 0 380 380" fill="none" xmlns="http://www.w3.org/2000/svg" className="why-svg">
        <defs>
          <linearGradient id="wg1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4B5320"/><stop offset="100%" stopColor="#D97A26"/>
          </linearGradient>
          <linearGradient id="wg2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#EEF0E2"/><stop offset="100%" stopColor="#FBF6EC"/>
          </linearGradient>
        </defs>
        <rect x="20" y="20" width="340" height="340" rx="24" fill="url(#wg2)" stroke="rgba(75,83,32,0.15)" strokeWidth="1.5"/>
        {[80,140,200,260,320].map(y => <line key={y} x1="20" y1={y} x2="360" y2={y} stroke="rgba(75,83,32,0.05)" strokeWidth="1"/>)}
        {[80,140,200,260,320].map(x => <line key={x} x1={x} y1="20" x2={x} y2="360" stroke="rgba(75,83,32,0.05)" strokeWidth="1"/>)}
        <rect x="155" y="155" width="70" height="70" rx="14" fill="url(#wg1)" opacity="0.9"/>
        <rect x="170" y="165" width="40" height="28" rx="8" fill="none" stroke="white" strokeWidth="2.5"/>
        <line x1="190" y1="165" x2="190" y2="155" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="190" cy="196" r="5" fill="white"/>
        <line x1="190" y1="196" x2="190" y2="204" stroke="white" strokeWidth="2"/>
        {[[60,60],[320,60],[60,320],[320,320]].map(([x,y],i) => (
          <g key={i}>
            <line x1={x} y1={y} x2="190" y2="190" stroke="url(#wg1)" strokeWidth="1" strokeDasharray="4 4" opacity="0.35"/>
            <circle cx={x} cy={y} r="16" fill="white" stroke="url(#wg1)" strokeWidth="2"/>
            {i===0 && <text x={x} y={y+5} textAnchor="middle" fontSize="14" fill="#4B5320">✓</text>}
            {i===1 && <text x={x} y={y+5} textAnchor="middle" fontSize="14" fill="#4B5320">★</text>}
            {i===2 && <text x={x} y={y+5} textAnchor="middle" fontSize="14" fill="#4B5320">⚡</text>}
            {i===3 && <text x={x} y={y+5} textAnchor="middle" fontSize="14" fill="#4B5320">◆</text>}
          </g>
        ))}
        {[[190,40],[340,190],[190,340],[40,190]].map(([x,y],i) => (
          <circle key={i} cx={x} cy={y} r="5" fill="#D97A26" opacity="0.6"/>
        ))}
        <circle cx="190" cy="190" r="55" stroke="url(#wg1)" strokeWidth="1" fill="none" opacity="0.25"/>
        <circle cx="190" cy="190" r="80" stroke="url(#wg1)" strokeWidth="1" fill="none" strokeDasharray="3 5" opacity="0.15"/>
      </svg>
    </div>
  );
}
